// ThemeContext.tsx
import React, { createContext, useMemo, useState, useContext, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CustomTheme from ".";

const ThemeModeContext = createContext({
  toggleTheme: () => {},
  mode: "light" as "light" | "dark",
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<"light" | "dark">("light");

  // Initialize theme from localStorage on mount and when username changes
  useEffect(() => {
    const updateTheme = () => {
      const username = localStorage.getItem("username");
      if (username) {
        const savedTheme = localStorage.getItem(`theme_${username}`);
        if (savedTheme === "dark" || savedTheme === "light") {
          setMode(savedTheme);
        } else {
          // Default to light theme for new users
          setMode("light");
          localStorage.setItem(`theme_${username}`, "light");
        }
      } else {
        // No user logged in, default to light theme
        setMode("light");
      }
    };

    updateTheme();

    // Listen for storage changes (when user logs in/out)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "username" || e.key?.startsWith("theme_")) {
        updateTheme();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    // Also listen for custom events (for same-tab user changes)
    const handleCustomStorageChange = () => {
      updateTheme();
    };

    window.addEventListener("userChanged", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userChanged", handleCustomStorageChange);
    };
  }, []);

  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    
    // Save theme preference for current user
    const username = localStorage.getItem("username");
    if (username) {
      localStorage.setItem(`theme_${username}`, newMode);
    }
  };

  const theme = useMemo(() => CustomTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={{ toggleTheme, mode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
